import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTablet } from '../contexts/TabletContext';
import NotificationCenter from './NotificationCenter';
import KitchenBarNotifications from './KitchenBarNotifications';
import EstablishmentSelector from './EstablishmentSelector';
import ActivityTicker from './ActivityTicker';
import { ServiceNotificationsButton } from './ServiceNotificationsPanel';
import MessagesBadge from './MessagesBadge';
import SecurityNotificationBell from './SecurityNotificationBell';
import { Input } from './ui/input';
import { getGabonTimeWithSeconds, getGabonShortDate } from '../utils/gabonTime';
import { 
    LayoutDashboard, 
    Package, 
    ShoppingCart, 
    ArrowLeftRight, 
    TrendingUp, 
    FileText, 
    Users, 
    User,
    LogOut,
    Menu,
    ShoppingBasket,
    UtensilsCrossed,
    ChefHat,
    ClipboardList,
    Settings,
    History,
    Clock,
    Calendar,
    DollarSign,
    UserCheck,
    Bell,
    CalendarCheck,
    Receipt,
    MessageSquare,
    AlertTriangle,
    Radio,
    Home,
    Send,
    Mail,
    Sun,
    Moon,
    MapPin,
    Truck,
    Brain,
    Wallet,
    Shield,
    Trophy,
    Building2,
    BookOpen,
    Heart,
    ChevronDown,
    ChevronRight,
    Store,
    CreditCard,
    PieChart,
    UserCog,
    BarChart3,
    Search,
    Plus,
    Zap,
    UserPlus,
    X,
    Loader2,
    Monitor,
    ClipboardCheck,
    Wine,
    GlassWater,
    Globe,
    Tag,
    Warehouse,
    Key,
    Tablet,
    PanelLeftClose,
    PanelLeft
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { getFilteredMenu, hasPermission } from '../config/menuConfig';

// ========================================
// QUICK ACTIONS CONFIGURATION
// ========================================

const getQuickActions = (role, navigate, isClockedIn = false) => {
    const isAdmin = role === 'ADMIN' || role === 'admin';
    const isDirecteur = role === 'DIRECTEUR';
    const isDirecteurAdjoint = role === 'DIRECTEUR_ADJOINT';
    const isServeur = role === 'SERVEUR';
    
    const actions = [];
    
    // SERVEUR: Seulement Pointer (et accès après pointage géré ailleurs)
    // DIRECTEUR / DIRECTEUR ADJOINT: Seulement Pointer
    // ADMIN: Aucune action rapide
    
    // Pointer - pour tous sauf Admin
    if (!isAdmin) {
        actions.push({
            id: 'clock',
            label: 'Pointer',
            shortLabel: 'Pointer',
            icon: UserPlus,
            color: 'bg-blue-500 hover:bg-blue-600',
            action: () => navigate('/clock')
        });
    }
    
    return actions;
};

// ========================================
// MENU CONFIGURATION - SYSTÈME UNIFIÉ
// ========================================
// Utilise la configuration centralisée de menuConfig.js
// Le menu est le même pour tous, seules les permissions filtrent les éléments visibles

const getMenuGroups = (role, isClockedIn = true) => {
    // SERVEUR who is NOT clocked in: show ONLY "Pointer" button
    if (role === 'SERVEUR' && !isClockedIn) {
        return [{
            id: 'pointage',
            label: 'POINTAGE OBLIGATOIRE',
            icon: Clock,
            color: 'text-red-500',
            bgColor: 'bg-red-500/10',
            items: [
                { path: '/clock', label: 'Pointer', icon: Clock }
            ]
        }];
    }
    
    // Utilise le système de menu unifié basé sur les permissions
    const filteredMenu = getFilteredMenu(role);
    
    // Si le menu filtré est vide (rôle non reconnu), fallback minimal
    if (!filteredMenu || filteredMenu.length === 0) {
        return [{
            id: 'default',
            label: 'MENU',
            icon: Home,
            color: 'text-gray-500',
            bgColor: 'bg-gray-500/10',
            items: [
                { path: '/clock', label: 'Pointer', icon: Clock },
                { path: '/messaging', label: 'Messagerie', icon: MessageSquare, hasBadge: true }
            ]
        }];
    }
    
    return filteredMenu;
};

// ========================================
// GLOBAL SEARCH COMPONENT
// ========================================
const GlobalSearch = ({ onClose }) => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState({ products: [], employees: [], tables: [] });
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    
    useEffect(() => {
        inputRef.current?.focus();
    }, []);
    
    useEffect(() => {
        const search = async () => {
            if (query.length < 2) {
                setResults({ products: [], employees: [], tables: [] });
                return;
            }
            
            setLoading(true);
            try {
                const [productsRes, employeesRes, tablesRes] = await Promise.all([
                    api().get(`/products?search=${encodeURIComponent(query)}&limit=5`).catch(() => ({ data: [] })),
                    api().get(`/staff?search=${encodeURIComponent(query)}`).catch(() => ({ data: [] })),
                    api().get('/tables').catch(() => ({ data: [] }))
                ]);
                
                const products = (productsRes.data || []).slice(0, 5);
                const employees = (employeesRes.data || []).filter(e => 
                    e.name?.toLowerCase().includes(query.toLowerCase())
                ).slice(0, 5);
                const tables = (tablesRes.data || []).filter(t => 
                    t.number?.toString().includes(query) || t.name?.toLowerCase().includes(query.toLowerCase())
                ).slice(0, 5);
                
                setResults({ products, employees, tables });
            } catch (error) {
                console.error('Search error:', error);
            } finally {
                setLoading(false);
            }
        };
        
        const debounce = setTimeout(search, 300);
        return () => clearTimeout(debounce);
    }, [query, api]);
    
    const handleSelect = (type, item) => {
        if (type === 'product') navigate(`/products?highlight=${item.id}`);
        else if (type === 'employee') navigate(`/staff?highlight=${item.id}`);
        else if (type === 'table') navigate(`/tables?table=${item.id}`);
        onClose?.();
    };
    
    const hasResults = results.products.length > 0 || results.employees.length > 0 || results.tables.length > 0;
    
    return (
        <div className="relative">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher..."
                    className="pl-9 pr-8 h-9 text-sm bg-muted/50 border-0 focus-visible:ring-1"
                    data-testid="global-search-input"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
            </div>
            
            {/* Search Results Dropdown */}
            {query.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                    {!hasResults && !loading && (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            Aucun résultat pour "{query}"
                        </div>
                    )}
                    
                    {/* Products */}
                    {results.products.length > 0 && (
                        <div>
                            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50">
                                PRODUITS
                            </div>
                            {results.products.map(product => (
                                <button
                                    key={product.id}
                                    onClick={() => handleSelect('product', product)}
                                    className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-2 text-sm"
                                >
                                    <Package className="w-4 h-4 text-orange-500" />
                                    <span>{product.name}</span>
                                    {product.quantity !== undefined && (
                                        <Badge variant="outline" className="ml-auto text-xs">
                                            {product.quantity} {product.unit}
                                        </Badge>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                    
                    {/* Employees */}
                    {results.employees.length > 0 && (
                        <div>
                            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50">
                                EMPLOYÉS
                            </div>
                            {results.employees.map(employee => (
                                <button
                                    key={employee.id}
                                    onClick={() => handleSelect('employee', employee)}
                                    className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-2 text-sm"
                                >
                                    <User className="w-4 h-4 text-purple-500" />
                                    <span>{employee.name}</span>
                                    <Badge variant="outline" className="ml-auto text-xs">
                                        {employee.role}
                                    </Badge>
                                </button>
                            ))}
                        </div>
                    )}
                    
                    {/* Tables */}
                    {results.tables.length > 0 && (
                        <div>
                            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50">
                                TABLES
                            </div>
                            {results.tables.map(table => (
                                <button
                                    key={table.id}
                                    onClick={() => handleSelect('table', table)}
                                    className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-2 text-sm"
                                >
                                    <MapPin className="w-4 h-4 text-green-500" />
                                    <span>Table {table.number || table.name}</span>
                                    <Badge 
                                        variant="outline" 
                                        className={cn(
                                            "ml-auto text-xs",
                                            (table.status === 'occupied' || table.status === 'occupee') && "text-red-500 border-red-500/50",
                                            table.status === 'available' && "text-green-500 border-green-500/50"
                                        )}
                                    >
                                        {(table.status === 'occupied' || table.status === 'occupee') ? 'Occupée' : 'Libre'}
                                    </Badge>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ========================================
// QUICK ACTIONS COMPONENT - Optimized for iPad sidebar
// ========================================

const QuickActions = ({ actions, onClose, isTablet = false }) => {
    if (actions.length === 0) return null;
    
    return (
        <div className={cn(
            "px-3 py-2 border-b border-border/50",
            isTablet && "px-2 py-2"
        )}>
            <p className={cn(
                "text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2",
                isTablet && "text-[9px] mb-1.5 px-1"
            )}>
                Actions rapides
            </p>
            <div className={cn(
                "flex gap-1.5",
                isTablet && "flex flex-row gap-1.5"
            )}>
                {actions.map(action => (
                    <button
                        key={action.id}
                        onClick={() => {
                            action.action();
                            onClose?.();
                        }}
                        className={cn(
                            "flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-white text-xs font-medium transition-all",
                            action.color,
                            // Tablet: compact inline buttons
                            isTablet && "h-8 gap-1 px-2 text-[10px] rounded-md font-semibold whitespace-nowrap"
                        )}
                        data-testid={`quick-action-${action.id}`}
                    >
                        <action.icon className={cn(
                            "w-3.5 h-3.5",
                            isTablet && "w-3.5 h-3.5 flex-shrink-0"
                        )} />
                        <span className={cn(
                            "hidden sm:inline",
                            isTablet && "inline"
                        )}>{isTablet ? action.shortLabel || action.label : action.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

// ========================================
// COLLAPSIBLE MENU GROUP COMPONENT - Tablet Optimized
// ========================================

const MenuGroup = ({ group, isOpen, onToggle, onItemClick, unreadMessages, currentPath, isFirst, isTablet = false, isCollapsed = false }) => {
    const hasActiveItem = group.items.some(item => currentPath === item.path);
    
    // In collapsed mode (tablet), show only icons with tooltip
    if (isCollapsed && isTablet) {
        return (
            <div className={cn(!isFirst && "border-t border-border/30 pt-2 mt-2")}>
                {/* Collapsed: Show icon only */}
                <div className="flex flex-col items-center gap-1 py-2">
                    {group.items.slice(0, 3).map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={onItemClick}
                            title={item.label}
                            className={({ isActive }) =>
                                cn(
                                    "w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-150 relative",
                                    "hover:bg-muted/50",
                                    isActive 
                                        ? "bg-primary/10 text-primary" 
                                        : "text-muted-foreground hover:text-foreground"
                                )
                            }
                            data-testid={`menu-item-collapsed-${item.path.replace(/\//g, '-')}`}
                        >
                            <item.icon className="w-5 h-5" />
                            {item.hasBadge && unreadMessages > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-white text-[10px] flex items-center justify-center font-bold">
                                    {unreadMessages > 9 ? '9+' : unreadMessages}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className={cn(!isFirst && "border-t border-border/30 pt-2 mt-2")}>
            {/* Group Header */}
            <button
                onClick={onToggle}
                className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200",
                    "hover:bg-muted/50 group",
                    hasActiveItem && !isOpen && group.bgColor,
                    // Tablet: larger touch target
                    isTablet && "min-h-[56px] px-4 py-3"
                )}
                data-testid={`menu-group-${group.id}`}
            >
                <div className="flex items-center gap-2">
                    <div className={cn(
                        "p-1 rounded",
                        group.bgColor,
                        isTablet && "p-2"
                    )}>
                        <group.icon className={cn(
                            "w-3.5 h-3.5",
                            group.color,
                            isTablet && "w-5 h-5"
                        )} />
                    </div>
                    <span className={cn(
                        "text-xs font-bold tracking-wide",
                        hasActiveItem ? "text-foreground" : "text-muted-foreground",
                        isTablet && "text-base"
                    )}>
                        {group.label}
                    </span>
                </div>
                <ChevronDown className={cn(
                    "w-4 h-4 text-muted-foreground transition-transform duration-200",
                    !isOpen && "-rotate-90",
                    isTablet && "w-5 h-5"
                )} />
            </button>
            
            {/* Group Items */}
            <div className={cn(
                "overflow-hidden transition-all duration-200 ease-in-out",
                isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
            )}>
                <div className={cn(
                    "mt-1 space-y-0.5 pl-2",
                    isTablet && "mt-2 space-y-1 pl-3"
                )}>
                    {group.items.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={onItemClick}
                            className={({ isActive }) =>
                                cn(
                                    "flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm transition-all duration-150",
                                    "hover:bg-muted/50",
                                    isActive 
                                        ? "bg-primary/10 text-primary font-medium" 
                                        : "text-muted-foreground hover:text-foreground",
                                    item.highlight && !isActive && "font-medium",
                                    // Tablet: larger touch targets
                                    isTablet && "min-h-[56px] gap-4 px-4 py-3 text-base rounded-xl"
                                )
                            }
                            data-testid={`menu-item-${item.path.replace(/\//g, '-')}`}
                        >
                            <item.icon className={cn(
                                "w-4 h-4 flex-shrink-0",
                                item.highlight && "text-primary",
                                isTablet && "w-5 h-5"
                            )} />
                            <span className="flex-1 truncate">{item.label}</span>
                            {item.hasBadge && unreadMessages > 0 && (
                                <Badge className={cn(
                                    "bg-destructive text-white text-[10px] px-1.5 min-w-4 h-4 flex items-center justify-center",
                                    isTablet && "text-xs px-2 min-w-5 h-5"
                                )}>
                                    {unreadMessages > 99 ? '99+' : unreadMessages}
                                </Badge>
                            )}
                        </NavLink>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ========================================
// NAV CONTENT COMPONENT - Tablet Optimized
// ========================================

const NavContent = ({ onClose, unreadMessages = 0, formatTime, formatDate, onContactDirection, isTablet = false, isClockedIn = true }) => {
    const { user, logout, loading: authLoading } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Attendre que l'utilisateur soit chargé avant d'afficher le menu
    // Cela évite le "flash" du menu admin pendant le chargement
    const role = user?.role || '';
    const isAdmin = role === 'ADMIN' || role === 'admin';
    const isServeur = role === 'SERVEUR';
    
    // Si l'utilisateur n'est pas encore chargé, ne pas afficher le menu
    // pour éviter le flash du menu par défaut
    const menuGroups = user ? getMenuGroups(role, isClockedIn) : [];
    const quickActions = user && (!isServeur || isClockedIn) ? getQuickActions(role, navigate) : [];
    
    // State for advanced settings dropdown
    const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
    
    // State for collapsed groups - default all open
    const [openGroups, setOpenGroups] = useState(() => {
        const initial = {};
        menuGroups.forEach(g => { initial[g.id] = true; });
        return initial;
    });
    
    const toggleGroup = (groupId) => {
        setOpenGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
    };
    
    const collapseAll = () => {
        const collapsed = {};
        menuGroups.forEach(g => { collapsed[g.id] = false; });
        setOpenGroups(collapsed);
    };
    
    const expandAll = () => {
        const expanded = {};
        menuGroups.forEach(g => { expanded[g.id] = true; });
        setOpenGroups(expanded);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        if (onClose) onClose();
    };

    return (
        <div className="flex flex-col h-full">
            {/* Logo & Establishment */}
            <div className={cn(
                "p-3 border-b border-border/50",
                isTablet && "p-4"
            )}>
                <div className={cn(
                    "flex items-center gap-2.5 mb-3",
                    isTablet && "gap-3 mb-4"
                )}>
                    <div className={cn(
                        "w-9 h-9 rounded-xl overflow-hidden bg-accent flex items-center justify-center",
                        isTablet && "w-12 h-12"
                    )}>
                        <img src="/icons/logo-small.png" alt="ALDRIN'S" className={cn(
                            "w-9 h-9 object-cover",
                            isTablet && "w-12 h-12"
                        )} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className={cn(
                            "font-display font-bold text-base text-foreground tracking-tight",
                            isTablet && "text-lg"
                        )}>
                            ALDRIN'S
                        </h1>
                        <p className={cn(
                            "text-[10px] text-muted-foreground",
                            isTablet && "text-xs"
                        )}>Restaurant POS</p>
                    </div>
                </div>
                <EstablishmentSelector />
            </div>
            
            {/* Quick Actions - Only show if there are actions (not for Admin) */}
            {quickActions.length > 0 && (
                <QuickActions actions={quickActions} onClose={onClose} isTablet={isTablet} />
            )}
            
            {/* Global Search - Hidden for Admin */}
            {!isAdmin && (
                <div className={cn(
                    "px-3 py-2 border-b border-border/50",
                    isTablet && "px-4 py-3"
                )}>
                    <GlobalSearch onClose={onClose} />
                </div>
            )}

            {/* Collapse/Expand Controls */}
            <div className={cn(
                "flex items-center justify-between px-3 py-1.5 bg-muted/30",
                isTablet && "px-4 py-2"
            )}>
                <span className={cn(
                    "text-[10px] font-semibold text-muted-foreground uppercase",
                    isTablet && "text-xs"
                )}>Menu</span>
                <div className="flex gap-1">
                    <button
                        onClick={collapseAll}
                        className={cn(
                            "text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded hover:bg-muted/50 transition-colors",
                            isTablet && "text-xs px-2 py-1"
                        )}
                    >
                        Réduire
                    </button>
                    <span className="text-muted-foreground/30">|</span>
                    <button
                        onClick={expandAll}
                        className={cn(
                            "text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded hover:bg-muted/50 transition-colors",
                            isTablet && "text-xs px-2 py-1"
                        )}
                    >
                        Déplier
                    </button>
                </div>
            </div>

            {/* Navigation Menu */}
            <nav className={cn(
                "flex-1 p-2 overflow-y-auto scrollbar-thin",
                isTablet && "p-3"
            )}>
                {menuGroups.map((group, index) => (
                    <MenuGroup
                        key={group.id}
                        group={group}
                        isOpen={openGroups[group.id]}
                        onToggle={() => toggleGroup(group.id)}
                        onItemClick={onClose}
                        unreadMessages={unreadMessages}
                        currentPath={location.pathname}
                        isFirst={index === 0}
                        isTablet={isTablet}
                    />
                ))}
            </nav>

            {/* User info with clock */}
            <div className={cn(
                "p-2 border-t border-border/50 bg-muted/20",
                isTablet && "p-4"
            )}>
                {/* Clock */}
                <div className={cn(
                    "text-center mb-2 pb-2 border-b border-border/30",
                    isTablet && "mb-3 pb-3"
                )}>
                    <p className={cn(
                        "text-lg font-mono font-bold text-primary",
                        isTablet && "text-xl"
                    )}>
                        {formatTime ? formatTime() : '--:--:--'}
                    </p>
                    <p className={cn(
                        "text-[10px] text-muted-foreground capitalize",
                        isTablet && "text-xs"
                    )}>
                        {formatDate ? formatDate() : ''}
                    </p>
                </div>
                
                {/* Admin-only: Paramètres avancés (dropdown) */}
                {isAdmin && (
                    <div className="mb-2">
                        <button
                            onClick={() => setAdvancedSettingsOpen(!advancedSettingsOpen)}
                            className={cn(
                                "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium",
                                "bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors",
                                isTablet && "py-3 text-base"
                            )}
                        >
                            <div className="flex items-center gap-2">
                                <Settings className={cn("w-4 h-4 text-slate-600", isTablet && "w-5 h-5")} />
                                <span className="text-slate-700 dark:text-slate-300">Paramètres avancés</span>
                            </div>
                            <ChevronDown className={cn(
                                "w-4 h-4 text-slate-500 transition-transform",
                                advancedSettingsOpen && "rotate-180",
                                isTablet && "w-5 h-5"
                            )} />
                        </button>
                        
                        {advancedSettingsOpen && (
                            <div className="mt-1 ml-2 space-y-1 border-l-2 border-slate-200 dark:border-slate-700 pl-2">
                                {/* Gestion Utilisateurs - Création de comptes */}
                                <button
                                    onClick={() => {
                                        navigate('/admin');
                                        onClose?.();
                                    }}
                                    className={cn(
                                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left hover:bg-muted/50 transition-colors",
                                        "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800",
                                        isTablet && "py-2 text-sm"
                                    )}
                                >
                                    <Users className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                    <span className="font-medium text-blue-700 dark:text-blue-400">Gestion Utilisateurs</span>
                                </button>
                                
                                {/* Guide d'utilisation */}
                                <button
                                    onClick={() => {
                                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/guide-utilisation`, '_blank');
                                    }}
                                    className={cn(
                                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left hover:bg-muted/50 transition-colors",
                                        isTablet && "py-2 text-sm"
                                    )}
                                >
                                    <FileText className="w-3.5 h-3.5 text-blue-500" />
                                    <span>Guide d'utilisation</span>
                                </button>
                                
                                {/* Identifiants Système */}
                                <button
                                    onClick={() => {
                                        navigate('/admin/credentials');
                                        onClose?.();
                                    }}
                                    className={cn(
                                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left hover:bg-muted/50 transition-colors",
                                        isTablet && "py-2 text-sm"
                                    )}
                                >
                                    <Key className="w-3.5 h-3.5 text-red-500" />
                                    <span>Identifiants système</span>
                                </button>
                                
                                {/* Gestion des Permissions */}
                                <button
                                    onClick={() => {
                                        navigate('/admin/permissions');
                                        onClose?.();
                                    }}
                                    className={cn(
                                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left hover:bg-muted/50 transition-colors",
                                        isTablet && "py-2 text-sm"
                                    )}
                                >
                                    <Shield className="w-3.5 h-3.5 text-amber-500" />
                                    <span>Gestion permissions</span>
                                </button>
                                
                                {/* Gestion des Rôles et Permissions */}
                                <button
                                    onClick={() => {
                                        navigate('/admin/roles-permissions');
                                        onClose?.();
                                    }}
                                    className={cn(
                                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left hover:bg-muted/50 transition-colors",
                                        isTablet && "py-2 text-sm"
                                    )}
                                >
                                    <Users className="w-3.5 h-3.5 text-indigo-500" />
                                    <span>Rôles et permissions</span>
                                </button>
                                
                                {/* Fiche technique */}
                                <button
                                    onClick={() => {
                                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/fiche-technique`, '_blank');
                                    }}
                                    className={cn(
                                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left hover:bg-muted/50 transition-colors",
                                        isTablet && "py-2 text-sm"
                                    )}
                                >
                                    <FileText className="w-3.5 h-3.5 text-purple-500" />
                                    <span>Fiche technique</span>
                                </button>
                                
                                {/* Guide interactif */}
                                <button
                                    onClick={() => {
                                        navigate('/guide');
                                        onClose?.();
                                    }}
                                    className={cn(
                                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left hover:bg-muted/50 transition-colors",
                                        isTablet && "py-2 text-sm"
                                    )}
                                >
                                    <Monitor className="w-3.5 h-3.5 text-green-500" />
                                    <span>Guide interactif</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
                
                {/* User Profile */}
                <div className="flex items-center justify-between">
                    <div 
                        className="flex-1 min-w-0 cursor-pointer hover:bg-muted/50 rounded-lg p-1.5 -m-1.5 transition-colors"
                        onClick={() => navigate('/profile')}
                        data-testid="sidebar-profile-link"
                    >
                        <p className="text-xs font-medium text-foreground truncate">
                            {user?.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">
                            {user?.email || user?.username}
                        </p>
                    </div>
                    <div className="flex items-center">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            className="text-muted-foreground hover:text-primary h-7 w-7"
                            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
                            data-testid="theme-toggle-btn"
                        >
                            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleLogout}
                            className="text-muted-foreground hover:text-destructive h-7 w-7"
                            data-testid="logout-btn"
                            title="Déconnexion"
                        >
                            <LogOut className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ========================================
// MAIN LAYOUT COMPONENT - Tablet Optimized
// ========================================

const Layout = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [showContactDialog, setShowContactDialog] = useState(false);
    const [contactMessage, setContactMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [isClockedIn, setIsClockedIn] = useState(true); // Default true, will be checked for SERVEUR
    const [clockCheckLoading, setClockCheckLoading] = useState(false);
    const { api, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const previousUnreadRef = useRef(-1);
    const notificationSoundRef = useRef(null);
    
    // Check if user is a server who needs to be clocked in
    const isServeur = user?.role === 'SERVEUR';
    const needsClockCheck = isServeur;
    
    // Check clock-in status for SERVEUR
    useEffect(() => {
        const checkClockStatus = async () => {
            if (!needsClockCheck || !api) return;
            
            setClockCheckLoading(true);
            try {
                const response = await api().get('/schedules/my-today');
                const data = response.data;
                
                // Check if user has clocked in today
                const hasClockIn = data?.attendance?.clock_in != null;
                const hasClockOut = data?.attendance?.clock_out != null;
                
                // User is considered "clocked in" if they have a clock_in time and no clock_out
                // Or if they're scheduled and present
                const clockedIn = hasClockIn && !hasClockOut;
                
                setIsClockedIn(clockedIn);
                
                // If not clocked in and not on clock page, redirect
                if (!clockedIn && location.pathname !== '/clock') {
                    navigate('/clock');
                }
            } catch (error) {
                console.error('Error checking clock status:', error);
                // On error, allow access (fail-open for better UX)
                setIsClockedIn(true);
            } finally {
                setClockCheckLoading(false);
            }
        };
        
        checkClockStatus();
    }, [needsClockCheck, api, location.pathname, navigate]);
    
    // Detect mobile
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Tablet mode detection
    const { isTablet, sidebarExpanded, toggleSidebar, expandSidebar, collapseSidebar, tabletMode, forceTabletMode, forceDesktopMode, setAutoMode } = useTablet();
    
    // Check if we're on a full-screen page (POS, Kitchen)
    const isFullScreenPage = ['/pos', '/kitchen-dashboard'].includes(location.pathname);
    
    // Clock update every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);
    
    // Format time for Gabon timezone
    const formatTime = () => {
        return currentTime.toLocaleTimeString('fr-FR', {
            timeZone: 'Africa/Libreville',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };
    
    const formatDate = () => {
        return currentTime.toLocaleDateString('fr-FR', {
            timeZone: 'Africa/Libreville',
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        });
    };
    
    // Send message to direction
    const handleSendToDirection = async () => {
        if (!contactMessage.trim()) {
            toast.error('Veuillez écrire un message');
            return;
        }
        
        setSendingMessage(true);
        try {
            const groupsRes = await api().get('/messaging/groups');
            let directionGroup = groupsRes.data?.find(g => g.name === 'Direction' || g.name === 'DIRECTION');
            
            if (!directionGroup) {
                const createRes = await api().post('/messaging/groups', {
                    name: 'Direction',
                    description: 'Messages pour la direction',
                    is_private: true
                });
                directionGroup = createRes.data;
            }
            
            await api().post('/messaging/messages', {
                group_id: directionGroup.id,
                content: `Message de ${user?.name}:\n\n${contactMessage}`
            });
            
            toast.success('Message envoyé à la direction');
            setContactMessage('');
            setShowContactDialog(false);
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error("Erreur lors de l'envoi du message");
        } finally {
            setSendingMessage(false);
        }
    };
    
    // Initialize notification sound
    useEffect(() => {
        notificationSoundRef.current = new Audio('/notification.wav');
        notificationSoundRef.current.volume = 0.5;
        
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);
    
    const playNotificationSound = () => {
        if (notificationSoundRef.current) {
            notificationSoundRef.current.currentTime = 0;
            notificationSoundRef.current.play().catch(() => {});
        }
    };
    
    // Fetch unread message count
    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const res = await api().get('/messaging/unread-count');
                const newUnread = res.data.unread_messages || 0;
                
                if (previousUnreadRef.current >= 0 && 
                    newUnread > previousUnreadRef.current && 
                    location.pathname !== '/messaging') {
                    
                    const diff = newUnread - previousUnreadRef.current;
                    playNotificationSound();
                    
                    if (Notification.permission === 'granted') {
                        const notification = new Notification("ALDRIN'S - Nouveau message", {
                            body: `Vous avez ${diff} nouveau${diff > 1 ? 'x' : ''} message${diff > 1 ? 's' : ''}`,
                            icon: '/logo192.png',
                            tag: 'new-message',
                            requireInteraction: false
                        });
                        
                        notification.onclick = () => {
                            window.focus();
                            window.location.href = '/messaging';
                            notification.close();
                        };
                    }
                }
                
                previousUnreadRef.current = newUnread;
                setUnreadMessages(newUnread);
            } catch (error) {
                console.error('Error fetching unread count:', error);
            }
        };
        
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 10000);
        return () => clearInterval(interval);
    }, [api, location.pathname]);
    
    // Send presence ping
    useEffect(() => {
        const sendPresencePing = async () => {
            try {
                const isMobile = window.innerWidth < 1024;
                await api().post('/messaging/presence/ping', {
                    device_type: isMobile ? 'mobile' : 'desktop',
                    device_id: localStorage.getItem('device_id') || 'unknown',
                    notifications_enabled: true
                });
            } catch (error) {
                console.error('Error sending presence ping:', error);
            }
        };
        
        if (!localStorage.getItem('device_id')) {
            localStorage.setItem('device_id', 'device_' + Math.random().toString(36).substr(2, 9));
        }
        
        sendPresencePing();
        const interval = setInterval(sendPresencePing, 30000);
        return () => clearInterval(interval);
    }, [api]);
    
    const isOnMessagingPage = location.pathname === '/messaging';
    
    // Auto-close mobile menu when route changes
    useEffect(() => {
        if (isMobile && mobileOpen) {
            setMobileOpen(false);
        }
    }, [location.pathname]);

    // Tablet sidebar width
    const tabletSidebarWidth = sidebarExpanded ? 'w-[280px]' : 'w-[72px]';

    return (
        <div className={cn(
            "min-h-screen bg-background overflow-x-hidden",
            isTablet && "tablet-mode tablet-scroll-lock"
        )}>
            {/* Contact Direction Dialog */}
            <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
                <DialogContent className={cn(
                    "max-w-md mx-4 sm:mx-auto",
                    isTablet && "max-w-lg"
                )}>
                    <DialogHeader>
                        <DialogTitle className={cn(
                            "flex items-center gap-2",
                            isTablet && "text-lg"
                        )}>
                            <Mail className={cn("w-5 h-5 text-amber-500", isTablet && "w-6 h-6")} />
                            Écrire à la Direction
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className={cn(
                            "text-sm text-muted-foreground",
                            isTablet && "text-base"
                        )}>
                            Votre message sera envoyé au Directeur et à l'Administrateur.
                        </p>
                        <Textarea
                            value={contactMessage}
                            onChange={(e) => setContactMessage(e.target.value)}
                            placeholder="Écrivez votre message ici..."
                            rows={5}
                            className={cn(
                                "resize-none text-base",
                                isTablet && "text-lg min-h-[150px]"
                            )}
                        />
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setShowContactDialog(false)} 
                            className={cn(
                                "w-full sm:w-auto",
                                isTablet && "min-h-[56px] text-base"
                            )}
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleSendToDirection}
                            disabled={sendingMessage || !contactMessage.trim()}
                            className={cn(
                                "w-full sm:w-auto",
                                isTablet && "min-h-[56px] text-base"
                            )}
                        >
                            {sendingMessage ? 'Envoi...' : (
                                <>
                                    <Send className={cn("w-4 h-4 mr-2", isTablet && "w-5 h-5")} />
                                    Envoyer
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* TABLET SIDEBAR - Always visible, fixed position */}
            {isTablet && !isFullScreenPage && (
                <aside 
                    className="fixed inset-y-0 left-0 z-50 w-[260px] bg-card border-r border-border/50 shadow-sm overflow-y-auto overflow-x-hidden tablet-sidebar-fixed"
                >
                    <NavContent 
                        unreadMessages={unreadMessages}
                        formatTime={formatTime}
                        formatDate={formatDate}
                        onContactDirection={() => setShowContactDialog(true)}
                        isTablet={true}
                        isClockedIn={isClockedIn}
                    />
                </aside>
            )}
            
            {/* Desktop Sidebar - Hidden on tablet (tablet uses its own sidebar) */}
            {!isTablet && (
                <aside className={cn(
                    "hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-60 lg:bg-card lg:border-r lg:border-border/50 lg:shadow-sm",
                    isFullScreenPage && "xl:block lg:hidden"
                )}>
                    <NavContent 
                        unreadMessages={unreadMessages}
                        formatTime={formatTime}
                        formatDate={formatDate}
                        onContactDirection={() => setShowContactDialog(true)}
                        isClockedIn={isClockedIn}
                    />
                </aside>
            )}

            {/* Mobile/Tablet Header */}
            <header className={cn(
                "fixed top-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-zinc-200",
                // Mobile: full width header with proper safe area
                !isTablet && "left-0 lg:hidden safe-area-top",
                // Tablet: header starts after fixed sidebar (260px)
                isTablet && !isFullScreenPage && "left-[260px]",
                isTablet && isFullScreenPage && "left-0",
                // Full screen pages
                !isTablet && isFullScreenPage && "xl:hidden lg:flex lg:flex-col"
            )}>
                {/* Main header row */}
                <div className={cn(
                    "h-16 flex items-center justify-between px-4",
                    isTablet && "h-14 px-4"
                )}>
                    {/* Mobile menu button - hidden on tablet (sidebar always visible) */}
                    {!isTablet && (
                        <div className="flex items-center gap-3">
                            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                                <SheetTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-12 w-12 rounded-xl bg-zinc-100 hover:bg-zinc-200 touch-target" 
                                        data-testid="burger-menu-toggle"
                                    >
                                        <Menu className="w-6 h-6 text-zinc-700" />
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="p-0 w-[85%] max-w-[320px]">
                                    <NavContent 
                                        onClose={() => setMobileOpen(false)} 
                                        unreadMessages={unreadMessages}
                                        formatTime={formatTime}
                                        formatDate={formatDate}
                                        onContactDirection={() => {
                                            setMobileOpen(false);
                                            setShowContactDialog(true);
                                        }}
                                        isMobile={true}
                                        isClockedIn={isClockedIn}
                                    />
                                </SheetContent>
                            </Sheet>
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-xl overflow-hidden bg-accent flex items-center justify-center shadow-sm">
                                    <img src="/icons/logo-small.png" alt="ALDRIN'S" className="w-9 h-9 object-cover" />
                                </div>
                                <span className="font-display font-bold text-base text-zinc-900 tracking-tight">ALDRIN'S</span>
                            </div>
                        </div>
                    )}
                    
                    {/* Tablet: Simple header with badge */}
                    {isTablet && (
                        <div className="flex items-center gap-3">
                            <span className="font-display font-bold text-base text-foreground">ALDRIN'S</span>
                            {/* Tablet mode indicator */}
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                <Tablet className="w-3 h-3 mr-1" />
                                iPad
                            </Badge>
                        </div>
                    )}
                    
                    {/* Clock - centered on mobile */}
                    <div className="flex-1 flex justify-center">
                        <span className={cn(
                            "text-sm font-mono font-semibold text-zinc-600 bg-zinc-100 px-3 py-1.5 rounded-lg",
                            isTablet && "text-base"
                        )}>{formatTime()}</span>
                    </div>
                    
                    {/* Action icons */}
                    <div className={cn(
                        "flex items-center gap-2",
                        isTablet && "gap-2"
                    )}>
                        {/* Search button on mobile */}
                        {!isTablet && (
                            <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => setShowMobileSearch(true)}
                                className="h-11 w-11 rounded-xl bg-zinc-100 hover:bg-zinc-200"
                                data-testid="mobile-search-btn"
                            >
                                <Search className="w-5 h-5 text-zinc-600" />
                            </Button>
                        )}
                        {/* Security notifications for Admin/Directeur */}
                        {['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role) && (
                            <SecurityNotificationBell />
                        )}
                        <MessagesBadge isTablet={isTablet} />
                        <NotificationCenter isTablet={isTablet} />
                    </div>
                </div>
            </header>

            {/* Main Content - Vertical scroll only */}
            <main className={cn(
                // Mobile - single column, full width, safe area padding
                "pt-[72px] pb-24 overflow-x-hidden",
                isMobile && "safe-area-bottom",
                // Desktop
                !isTablet && !isMobile && "lg:pl-60 lg:pt-0 lg:pb-0",
                // Tablet - fixed sidebar (260px), content scrolls vertically only
                isTablet && !isFullScreenPage && "pl-[260px]",
                isTablet && "pt-[72px] overflow-x-hidden",
                // Full screen pages
                !isTablet && isFullScreenPage && "lg:pl-0 lg:pt-14 xl:pl-60 xl:pt-0"
            )}>
                {/* Desktop Activity Ticker + Back button */}
                {!isTablet && !isMobile && location.pathname !== '/dashboard' && location.pathname !== '/' && !isFullScreenPage && (
                    <div className="hidden lg:flex px-6 pt-4 pb-0 items-center justify-between">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            className="text-muted-foreground hover:text-primary h-8 text-xs"
                        >
                            <Home className="w-3.5 h-3.5 mr-1.5" />
                            Retour au tableau de bord
                        </Button>
                        <ActivityTicker />
                    </div>
                )}
                {/* Desktop Activity Ticker on Dashboard */}
                {!isTablet && !isMobile && (location.pathname === '/dashboard' || location.pathname === '/') && !isFullScreenPage && (
                    <div className="hidden lg:flex px-6 pt-4 pb-0 justify-end">
                        <ActivityTicker />
                    </div>
                )}
                
                {/* SERVEUR NOT CLOCKED IN - Block Screen */}
                {isServeur && !isClockedIn && location.pathname !== '/clock' ? (
                    <div className="flex-1 flex items-center justify-center p-8">
                        <div className="max-w-md text-center space-y-6">
                            <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                                <Clock className="w-10 h-10 text-red-500" />
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-2xl font-bold text-red-600">Pointage Requis</h2>
                                <p className="text-muted-foreground">
                                    Vous devez pointer votre arrivée avant d'accéder au système.
                                </p>
                            </div>
                            <Button 
                                size="lg"
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => navigate('/clock')}
                            >
                                <Clock className="w-5 h-5 mr-2" />
                                Aller au pointage
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className={cn(
                        // Mobile: generous padding, single column
                        "px-4 py-4",
                        isMobile && "mobile-single-col mobile-gap",
                        // Tablet/Desktop
                        !isMobile && "sm:p-4 lg:p-6 lg:pt-3",
                        isTablet && "p-5",
                        isFullScreenPage && "p-0 sm:p-0 lg:p-0"
                    )}>
                        <Outlet />
                    </div>
                )}
            </main>
            
            {/* Mobile Floating Button - Messagerie SUPPRIMÉ sur mobile pour UX propre */}
            {/* Les actions sont accessibles via le menu ou la barre du haut */}
            
            {/* Tablet Quick Actions FAB */}
            {isTablet && !isFullScreenPage && (
                <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
                    {/* New Sale FAB */}
                    <button
                        onClick={() => navigate('/pos')}
                        className="w-16 h-16 rounded-2xl bg-green-600 text-white shadow-lg flex items-center justify-center hover:bg-green-700 transition-colors"
                        data-testid="tablet-fab-new-sale"
                    >
                        <Plus className="w-8 h-8" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Layout;
